import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { HeroCommonExtras, AnimatedHeadlineWords, cmsUrl } from './HeroBase';
import { ConfettiCanvas } from '../ConfettiCanvas';

// Video-background hero — uses its OWN background (the video itself) so we
// don't pass through HeroBase. We still render the shared extras on dark.
export const HeroVideo = ({ cms = {}, onPrimary, onOpenAvailability }) => {
  const v = cms.variants?.video || {};
  const overlay = Math.min(0.9, Math.max(0, Number(v.overlay_opacity ?? 0.45)));
  const primaryCta = cms.primary_cta_text   || 'Build Your Dream Party';
  const secondCta  = cms.secondary_cta_text || 'Check Availability';
  const confettiOn = cms.confetti_enabled !== false;
  const extras = HeroCommonExtras({ cms, align: 'center', onDark: true });

  return (
    <section
      className="relative min-h-[88vh] flex items-center justify-center overflow-hidden bg-[#0F2A44]"
      data-testid="landing-hero"
      data-variant="video"
    >
      {v.video_url ? (
        <video
          src={cmsUrl(v.video_url)}
          poster={cmsUrl(v.poster_url)}
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden
          data-testid="hero-bg-video"
        />
      ) : (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: v.poster_url
              ? `url(${cmsUrl(v.poster_url)})`
              : 'linear-gradient(135deg, #0F2A44 0%, #FF3B6B 100%)',
          }}
          aria-hidden
        />
      )}
      <div className="absolute inset-0" style={{ background: `rgba(15,42,68,${overlay})` }} aria-hidden />
      {confettiOn && <ConfettiCanvas count={100} />}

      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="relative z-10 max-w-3xl text-center px-6 py-20"
      >
        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-white/85 mb-4 backdrop-blur-sm bg-white/10 rounded-full px-4 py-1" data-testid="hero-badge">
          {cms.badge_text || 'Creative Birthdays · Oakville'}
        </span>
        <AnimatedHeadlineWords
          cms={cms}
          onDark
          align="center"
          className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] mb-5"
          showEmoji={false}
        />
        <div className="mx-auto max-w-2xl">
          <p className="text-base sm:text-xl text-white/90 mb-4" data-testid="hero-subtext">{cms.subheadline || ''}</p>
          <div className="flex justify-center">{extras.location}</div>
          <div className="flex justify-center">{extras.pills({ when: 'above' })}</div>
        </div>
        <div className="flex flex-wrap gap-3 justify-center mt-2">
          <Button onClick={onPrimary} size="lg" className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-7" data-testid="hero-primary-cta">
            <Calendar className="w-5 h-5 mr-2" /> {primaryCta}
          </Button>
          <Button onClick={onOpenAvailability} size="lg" className="rounded-full bg-white/90 hover:bg-white text-[#0F2A44] px-7" data-testid="hero-secondary-cta">
            <CalendarDays className="w-5 h-5 mr-2" /> {secondCta}
          </Button>
        </div>
        <div className="flex justify-center mt-4">{extras.pills({ when: 'below' })}</div>
        <div className="flex justify-center">{extras.trustBadges}</div>
      </motion.div>
    </section>
  );
};

export default HeroVideo;
